/**
 * SessionStateManager — persists app state to localStorage so the
 * experience survives page reloads, tab switches, and browser crashes.
 *
 * Saved state:
 *   - appState: idle | running | paused
 *   - sessionName: pool instance name (e.g. "guide-3")
 *   - lastTopic: title, subtitle, image, wikipedia
 *   - lastPosition: lat, lon
 *   - lastResponse: content text
 *   - settings: transport, mode, track, interval
 *   - timestamp: when state was last saved
 */
const STORAGE_KEY = 'audio-guide-session-state';
const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes — matches pool idle timeout
const DEFAULT_STATE = {
    appState: 'idle',
    sessionName: null,
    lastTopic: null,
    lastPosition: null,
    lastResponse: null,
    settings: {
        transport: 'seated',
        mode: 'culture',
        trackId: null,
        intervalSec: 45,
    },
    timestamp: 0,
};
export class SessionStateManager {
    constructor() {
        this._state = this._load();
    }
    /** Current persisted state */
    get state() {
        return this._state;
    }
    /** Is there a valid saved session that can be resumed? */
    get canResume() {
        if (this._state.appState === 'idle')
            return false;
        if (!this._state.sessionName)
            return false;
        if (Date.now() - this._state.timestamp > MAX_AGE_MS)
            return false;
        return true;
    }
    /** Update app state */
    setAppState(state) {
        this._state.appState = state;
        this._save();
    }
    /** Save pool session name */
    setSessionName(name) {
        this._state.sessionName = name;
        this._save();
    }
    /** Save last topic card data */
    setLastTopic(topic) {
        this._state.lastTopic = topic;
        this._save();
    }
    /** Save last GPS position */
    setLastPosition(lat, lon) {
        this._state.lastPosition = { lat, lon };
        this._save();
    }
    /** Save last response content */
    setLastResponse(content) {
        this._state.lastResponse = content;
        this._save();
    }
    /** Save settings */
    setSettings(partial) {
        Object.assign(this._state.settings, partial);
        this._save();
    }
    /** Clear everything — used on Stop */
    clear() {
        this._state = { ...DEFAULT_STATE, timestamp: Date.now() };
        this._save();
    }
    _save() {
        this._state.timestamp = Date.now();
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this._state));
        }
        catch { }
    }
    _load() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw)
                return { ...DEFAULT_STATE };
            const parsed = JSON.parse(raw);
            // Expired?
            if (Date.now() - parsed.timestamp > MAX_AGE_MS) {
                return { ...DEFAULT_STATE };
            }
            return { ...DEFAULT_STATE, ...parsed };
        }
        catch {
            return { ...DEFAULT_STATE };
        }
    }
}
//# sourceMappingURL=SessionStateManager.js.map